import type { LlmProvider } from "../api/llm-usage.api";

/**
 * The vendors this page can draw a glyph and a name for. "unknown" is a real
 * state rather than a failure: `llm_usage` records whatever id the call site
 * used, so a model added to a worker before this catalog hears about it still
 * has to render.
 */
export type ModelBrand = "anthropic" | "google" | "unknown";

const PROVIDER_BRANDS: Record<LlmProvider, ModelBrand> = {
  ANTHROPIC: "anthropic",
  GEMINI: "google",
};

export function brandForProvider(provider: LlmProvider): ModelBrand {
  return PROVIDER_BRANDS[provider] ?? "unknown";
}

/**
 * `GEMINI` → Gemini. The column stores the enum the call was recorded under;
 * nothing outside the database calls a vendor by a shouted name. Untranslated
 * on purpose — these are trademarks, and they read the same in every locale.
 */
const PROVIDER_NAMES: Record<LlmProvider, string> = {
  ANTHROPIC: "Anthropic",
  GEMINI: "Gemini",
};

export function humanizeProvider(provider: LlmProvider): string {
  return PROVIDER_NAMES[provider] ?? humanizeModelId(provider.toLowerCase());
}

/**
 * The timeseries stacks by model id alone — a bucket carries no provider — so
 * the brand has to come off the id in the chart, where the tables can read it
 * from the row.
 */
export function brandForModelId(modelId: string): ModelBrand {
  const id = modelId.trim().toLowerCase();
  if (id.startsWith("claude-")) return "anthropic";
  if (id.startsWith("gemini-")) return "google";
  return "unknown";
}

/**
 * claude-opus-4-1 → Claude Opus 4.1; gemini-2.5-flash → Gemini 2.5 Flash.
 *
 * Derived rather than looked up, so a model nobody has written copy for yet
 * still reads as a name. The raw id stays reachable — every caller keeps it on
 * `title` — because it is the key the rate is set against.
 */
export function humanizeModelId(id: string): string {
  return (
    id
      // Vertex version suffixes (`@20251101`) are a pin, not part of the name.
      .replace(/@.*$/, "")
      // Join dotted version segments before title-casing (4-1 → 4.1, not "4 1").
      .replace(/(\d+)-(\d+)(?=-|$)/g, "$1.$2")
      .split("-")
      .map((part) => {
        if (/^\d+(\.\d+)?$/.test(part)) return part;
        if (part.length <= 2) return part.toUpperCase();
        return part[0]!.toUpperCase() + part.slice(1);
      })
      .join(" ")
  );
}
